// Refactored WarningNotification component using DataGridPro UI style from AlertNotificationTab
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { observer } from "mobx-react-lite";
import WarningStore from "../../stores/WarningStore";
import UserStore from "../../stores/UserStore";

const WarningNotification = observer(() => {
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState(null);
  const [newWarning, setNewWarning] = useState({
    field: "",
    likelihood: "",
    message: "",
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    WarningStore.startRealtime();
    WarningStore.loadReadWarningIds();
    WarningStore.loadWarnings();
    return () => WarningStore.stopRealtime();
  }, []);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const handleOpenViewModal = async (warning) => {
    setSelectedWarning(warning);
    await WarningStore.loadReaders(warning._id);
    setOpenViewModal(true);
  };
  const handleCloseViewModal = () => setOpenViewModal(false);
  const handleCreateWarning = async () => {
    await WarningStore.createWarning({ ...newWarning });
    setNewWarning({ field: "", likelihood: "", message: "" });
    setOpenModal(false);
  };

  const rows = useMemo(() => {
    return WarningStore.warningsList.map((w) => ({
      id: w._id,
      field: w.field,
      likelihood: w.likelihood,
      createdBy: w.created_by,
      isRead: WarningStore.readIds.has(w._id),
      raw: w,
    }));
  }, [WarningStore.warningsList, WarningStore.readIds]);

  const columns = useMemo(
    () => [
      {
        field: "field",
        headerName: "Field",
        flex: 1,
        renderCell: (params) => (
          <Typography fontWeight={params.row.isRead ? "normal" : 600}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "likelihood",
        headerName: "Likelihood",
        flex: 1,
        renderCell: (params) => (
          <Chip label={params.value} color="warning" size="small" />
        ),
      },
      {
        field: "createdBy",
        headerName: "Created By",
        flex: 1,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 180,
        renderCell: (params) => (
          <Box display="flex" gap={1} alignItems="center">
            <Tooltip title="View Warning">
              <IconButton onClick={() => handleOpenViewModal(params.row.raw)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            {params.row.isRead ? (
              <Button
                size="small"
                onClick={() => WarningStore.markAsUnread(params.row.id)}
              >
                Mark Unread
              </Button>
            ) : (
              <Button
                size="small"
                onClick={() => WarningStore.markAsRead(params.row.id)}
              >
                Mark Read
              </Button>
            )}
          </Box>
        ),
      },
    ],
    []
  );

  const paginatedRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(rows.length / pageSize);

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Warning Notifications</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => {
              WarningStore.loadReadWarningIds();
              WarningStore.loadWarnings();
            }}
          >
            Refresh
          </Button>
          <Button variant="contained" onClick={handleOpenModal}>
            Create Warning
          </Button>
        </Box>
      </Box>

      <Box mt={3} sx={{ height: 540, width: "100%" }}>
        <DataGridPro
          rows={paginatedRows}
          columns={columns}
          pagination={false}
          hideFooter
          getRowClassName={(params) =>
            !params.row.isRead ? "unread-alert-row" : ""
          }
          sx={{
            borderRadius: 2,
            boxShadow: 2,
            border: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
            "& .unread-alert-row": {
              borderLeft: "4px solid #1976d2",
              backgroundColor: "#ffffff !important",
              fontWeight: 600,
            },
          }}
        />
      </Box>

      <Box
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Button
          onClick={() => setPage(Math.max(page - 1, 1))}
          disabled={page === 1}
        >
          Prev
        </Button>
        <Box>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <Button
              key={p}
              onClick={() => setPage(p)}
              variant={p === page ? "contained" : "text"}
            >
              {p}
            </Button>
          ))}
        </Box>
        <Button
          onClick={() => setPage(Math.min(page + 1, totalPages))}
          disabled={page === totalPages}
        >
          Next
        </Button>
        <Box display="flex" gap={1} alignItems="center">
          <Typography>Rows per page:</Typography>
          <Select
            value={pageSize}
            size="small"
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* Create Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          p={4}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            width: 400,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Create Warning
          </Typography>
          <TextField
            label="Field"
            fullWidth
            value={newWarning.field}
            onChange={(e) =>
              setNewWarning({ ...newWarning, field: e.target.value })
            }
            margin="normal"
          />
          <TextField
            label="Likelihood"
            fullWidth
            value={newWarning.likelihood}
            onChange={(e) =>
              setNewWarning({ ...newWarning, likelihood: e.target.value })
            }
            margin="normal"
          />
          <TextField
            label="Message"
            fullWidth
            value={newWarning.message}
            onChange={(e) =>
              setNewWarning({ ...newWarning, message: e.target.value })
            }
            margin="normal"
          />
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              onClick={handleCreateWarning}
              variant="contained"
              sx={{ ml: 2 }}
            >
              Create
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* View Modal */}
      <Modal open={openViewModal} onClose={handleCloseViewModal}>
        <Box
          p={4}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            width: 500,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            color="primary"
            gutterBottom
          >
            Warning Details
          </Typography>

          {selectedWarning && (
            <Box>
              <Typography variant="subtitle1">
                <strong>Field:</strong> {selectedWarning.field}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Likelihood:</strong> {selectedWarning.likelihood}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Created By:</strong> {selectedWarning.created_by}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Message:</strong>{" "}
                {selectedWarning.message || "No message"}
              </Typography>

              <Box mt={3}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="secondary"
                >
                  Read by Users
                </Typography>
                {WarningStore.readersMap.get(selectedWarning._id)?.length >
                0 ? (
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                    p={1}
                    border="1px solid #ccc"
                    borderRadius={1}
                    bgcolor="#f9f9f9"
                  >
                    {WarningStore.readersMap
                      .get(selectedWarning._id)
                      .map((userId) => (
                        <Box
                          key={userId}
                          px={2}
                          py={0.5}
                          bgcolor="#e0f7fa"
                          borderRadius={1}
                          fontSize={14}
                          color="#00796b"
                        >
                          {UserStore.getUserNameById(userId)}
                        </Box>
                      ))}
                  </Box>
                ) : (
                  <Typography fontSize={14} color="text.secondary">
                    No users have read this warning yet.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button onClick={handleCloseViewModal} variant="outlined">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
});

export default WarningNotification;
